import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MOTIVOS_ENCERRAMENTO,
  ROTULO_ETAPA,
  ROTULO_MOTIVO_ENCERRAMENTO,
  exigeMotivo,
  type EtapaFunil,
} from '@reducee/contracts';
import { useState, type FormEvent } from 'react';

import { chamarApi, ErroDaApi } from '../api/cliente.ts';
import { CampoSelecao } from './Campo.tsx';
import { Dialogo } from './Dialogo.tsx';

/** A venda que está sendo movida, no mínimo que a janela precisa saber. */
export type OportunidadeEmMovimento = {
  id: string;
  contato: { nome: string };
};

/**
 * Confirma o movimento que o arrasto pediu, nos dois casos em que ele não é
 * barato de desfazer.
 *
 * Quase todo arrasto move sozinho — o ADR 0011 conta por quê. Esta janela só
 * aparece em Reciclagem, para colher o motivo, e em Fechado, para avisar antes
 * de criar o Contrato. Em ambos, um campo ou nenhum: arrastar é gesto de mover,
 * e a ficha da venda, com seus 26 campos, é para editar.
 *
 * Nada além da etapa viaja daqui. Quem quer corrigir a venda abre a ficha pelo
 * clique no cartão, que é onde ela mora.
 */
export function MoverEtapa({
  oportunidade,
  destino,
  aoFechar,
}: {
  oportunidade: OportunidadeEmMovimento;
  destino: EtapaFunil;
  aoFechar: () => void;
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);

  const pedeMotivo = exigeMotivo(destino);

  const mover = useMutation({
    mutationFn: (campos: FormData) => {
      const texto = (nome: string): string | undefined =>
        String(campos.get(nome) ?? '').trim() || undefined;

      return chamarApi(`/oportunidades/${oportunidade.id}/etapa`, {
        metodo: 'PATCH',
        // Sem próximo passo nem data: mudar de etapa os apaga de propósito, e
        // esta janela não os coleta. Quem ficar sem compromisso aparece no
        // indicador do painel e com aviso no cartão.
        corpo: { etapa: destino, motivoEncerramento: texto('motivoEncerramento') },
      });
    },
    onSuccess: async () => {
      // Fechar cria o Contrato e abre a Implantação na mesma transação, então
      // as telas de pós-venda também envelhecem com este gesto.
      await clienteDeConsultas.invalidateQueries({ queryKey: ['oportunidades'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['contatos'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['dashboard'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['atender-agora'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['implantacoes'] });
      aoFechar();
    },
    onError: (causa: unknown) => {
      setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível mover a Oportunidade.');
    },
  });

  function aoEnviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    setErro(null);
    mover.mutate(new FormData(evento.currentTarget));
  }

  return (
    <Dialogo
      area="Comercial"
      titulo={`Mover para ${ROTULO_ETAPA[destino]}`}
      descricao={oportunidade.contato.nome}
      aoFechar={aoFechar}
    >
      <form onSubmit={aoEnviar} data-teste="formulario-mover-etapa">
        {pedeMotivo ? (
          <CampoSelecao
            rotulo="Motivo de perda / reciclagem"
            name="motivoEncerramento"
            required
            autoFocus
            defaultValue=""
          >
            <option value="" disabled>
              Selecione
            </option>
            {MOTIVOS_ENCERRAMENTO.map((motivo) => (
              <option key={motivo} value={motivo}>
                {ROTULO_MOTIVO_ENCERRAMENTO[motivo]}
              </option>
            ))}
          </CampoSelecao>
        ) : null}

        {/* Fechado não cobra campo nenhum, e é o gesto de maior consequência da
          * tela: avisa o que vai acontecer antes de acontecer. */}
        {destino === 'FECHADO' ? (
          <p className="rounded-[9px] border border-linha bg-marinho-suave px-3 py-2.5 text-[13px]">
            Fechar esta venda cria o Contrato e abre a Implantação.
          </p>
        ) : null}

        {erro ? (
          <p role="alert" className="mt-4 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
            {erro}
          </p>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={aoFechar}
            className="rounded-[10px] border border-linha bg-superficie px-3.25 py-2.5 text-base font-[750] transition hover:bg-marinho-suave"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={mover.isPending}
            className="rounded-[10px] bg-marinho px-3.25 py-2.5 text-base font-[750] text-white transition hover:bg-marinho-claro disabled:opacity-60"
          >
            {destino === 'FECHADO' ? 'Fechar venda' : 'Mover'}
          </button>
        </div>
      </form>
    </Dialogo>
  );
}
